import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-community'
import XLSX from 'xlsx'
import Dayjs from 'dayjs'
import _ from 'lodash'
import StringFilter from './Filter/StringFilter.vue'
import NumberFilter from './Filter/NumberFilter.vue'
import DateFilter from './Filter/DateFilter.vue'
import BriqueGridCustomFilter from './BriqueGridCustomFilter.js'
import ButtonRenderer from './CellRenderer/ButtonRenderer.js'
import NumericEditor from './Editor/NumericEditor.js'
import DatePickerEditor from './Editor/DatePickerEditor.js'
import DateTimePickerEditor from './Editor/DateTimePickerEditor.js'

/**
 * 이 주석은 다른 파일에 주석을 달기 위한 가이드 역할을 합니다.
 * JSDoc으로 작성한 주석은 향후 HTML로 추출될 수 있기 때문에 최초작성자/최초작성일 등은 기술하지 않습니다.
 * 이러한 정보는 git의 History(log) 기능을 이용하여 조회할 수 있습니다.
 *
 * .vue 파일과 .js 파일은 반드시 분리하며 주석은 .js에만 기술하면 됩니다.
 *
 * Method 상단에는 @param과 @returns만 기술합니다.
 * Member는 옆에 단일라인 주석(//)을 사용하여 기술합니다.
 *
 * 아래는 실제 BriqueGrid에 대한 설명입니다.
 * ====================================
 * BriqueGrid Component
 * [2022-01-12]
 *  - innertext 표시 모두 주석처리 함
 * [2022-04-14]
 *  - TODO: 추후에 floating filter 적용을 한다면, Custom Floating Filter Component를 구성해야 합니다.
 *  - TODO: this.gridApi.paginationSetPageSize(Number(value)); 적용 필요 (pagination 사용 시 add해서 pagesize가 넘어가면 다음 페이지로 add)
 */
export default {
  name: 'BriqueGrid',
  components: {
    'ag-grid-vue': AgGridVue,
    stringFilter: StringFilter,
    numberFilter: NumberFilter,
    dateFilter: DateFilter,
    briqueGridCustomFilter: BriqueGridCustomFilter,
    buttonRenderer: ButtonRenderer,
    numericEditor: NumericEditor,
    datePickerEditor: DatePickerEditor,
    dateTimePickerEditor: DateTimePickerEditor
  },
  // mixin: [Grid], //Mixin을 하지 않아도 잘 됨
  inheritAttrs: false, // 부모 Component에서 지정한 Element 속성을 무시 - vue 파일의 다음 구분 참조(v-bind="$attrs", v-on="$listeners")
  props: {
    /**
     * Column Array
     * AgGrid에서 사용하는 Column data를 전달 받는다.
     * 추가 옵션(required, modifiable 등) 사용을 위하여 props에 지정함.
     */
    briqueGridColumns: {
      type: Array,
      default () {
        return []
      }
    },

    /**
     * Index Column Visible
     * AgGrid의 index columns을 설정할지 여부를 지정함.
     */
    indexColumnVisible: {
      type: Boolean,
      default: true,
      required: false
    },

    /**
     * Grid Row Data Array
     * AgGrid에서 사용하는 row data를 전달 받는다.
     * data reset을 위하여 props에 지정함.
     */
    rowData: {
      type: Array,
      default () {
        return []
      }
    },

    /**
     * Pagenation 옵션
     * AgGrid의 pagination 옵션을 true로 유지해야 한다.
     * AgGrid의 pagination 옵션은 Summary와 Pagination 부분을 모두 보게 하는 옵션이므로 briqueGridIsViewPagination과 briqueGridIsViewSummary로 두 기능을 분리하였다.
     */
    briqueGridIsViewPagination: {
      type: Boolean,
      default: false,
      required: false
    },
    /**
     * Pagination Type 지정
     * server-side의 경우 custom pagination을 사용
     * client-side의 경우 ag grid에서 기본 제공하는 pagination 사용
     */
    briqueGridPaginationType: {
      type: String,
      default: 'client', // server, client
      require: false
    },

    /**
     * Summary 보기 옵션
     *  - TODO: server side pagination에 대한 처리 필요
     */
    briqueGridIsViewSummary: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Total Count 보기 옵션
     */
    briqueGridIsViewTotalCount: {
      type: Boolean,
      default: true,
      required: false
    },

    /**
     * CUD Count 보기 옵션
     */
    briqueGridIsViewCudCount: {
      type: Boolean,
      default: true,
      required: false
    },

    /**
     * 선택된 수 보기 옵션
     */
    briqueGridIsViewSelectedCount: {
      type: Boolean,
      default: true,
      required: false
    },

    /**
     * 헤더 영역 보기 옵션
     */
    briqueGridIsViewHeader: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * 헤더의 타이틀
     */
    briqueGridTitle: {
      type: String,
      required: false
    },

    /**
     * 헤더의 타이틀 좌측 아이콘
     */
    briqueGridTitleIcon: {
      type: String,
      required: false
    },

    /**
     * Import Button 표시 유무
     */
    briqueGridIsViewImportBtn: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Export Button 표시 유무
     */
    briqueGridIsViewExportBtn: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Refresh Button 버튼 클릭시 사용할 Function
     * 이 Function은 async로 작성되어야 한다.
     */
    briqueGridRefreshFunction: {
      type: Function,
      required: false
    },

    /**
     * Restore Button 표시 유무
     */
    briqueGridIsViewRestoreBtn: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Toggle Column에 사용할 컬럼들 지정
     */
    briqueGridHideToggleColumns: {
      type: Array,
      required: false
    },

    /**
     * Add Row Button 표시 유무
     */
    briqueGridIsViewAddRowBtn: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Remove Row Button 표시 유무
     */
    briqueGridIsViewRemoveRowBtn: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Undo/Redo Button 표시 유무
     */
    briqueGridIsViewUndoRedoBtn: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Cell Value Change Custom Function
     * 이 Function은 async로 작성되어야 한다.
     */
    briqueGridCellValueChangedFunction: {
      type: Function,
      required: false
    },

    /**
     * pagination 관련 props (임시 추가해둠)
     */
    // pagingType: {
    //   type: String,
    //   default: 'none',
    //   validator: (val) => ['none', 'load-more', 'pagination'].includes(val)
    // },
    pagingSize: {
      type: Number,
      default: 50
    },
    totalElementCount: {
      type: Number,
      default: 1
    },
    pagingRangeBefore: {
      type: Number,
      default: 10
    },
    pagingRangeAfter: {
      type: Number,
      default: 10
    },
    currentPage: {
      type: Number,
      default: 0
    },
    totalPage: {
      type: Number,
      default: 1
    }
  }, // end props

  beforeMount () {
    this.frameworkComponents = {
      buttonRenderer: ButtonRenderer
    }
  },

  watch: {
    rowData (newVal, oldVal) {
      const changeRowData = newVal !== oldVal
      if (changeRowData) {
        this.resetData()
        this.initBriquegridDisplayArea()
        if (this.briqueGridIsViewTotalCount) {
          this.briqueGridTag.querySelector('.brique-grid-totalcount').innerText = newVal.length
        }
        // pagination description data 변경
        if (this.briqueGridIsViewPagination && this.briqueGridPaginationType === 'server') {
          this.setPagingDescBtn()
        }
      }
    },
    totalElementCount (newval) {
      this.paging.total = newval
    },
    currentPage (val) {
      this.paging.currentPageNumber = val + 1
    },
    totalPage (val) {
      this.paging.totalPageNumber = val
    }
  },
  computed: {
  }, // end - computed

  methods: {
    /**
     * BriqueGrid에서 추가한 화면 부분 초기화
     */
    initBriquegridDisplayArea () {
      this.removedData = []

      if (this.briqueGridIsViewCudCount) {
        this.briqueGridTag.querySelector('.brique-grid-ccount').innerText = '0'
        this.briqueGridTag.querySelector('.brique-grid-ucount').innerText = '0'
        this.briqueGridTag.querySelector('.brique-grid-dcount').innerText = '0'
        this.briqueGridTag.querySelector('.brique-grid-selected-rows-view-btn').classList.remove('active')
        this.briqueGridTag.querySelector('.brique-grid-updated-rows-view-btn').classList.remove('active')
      }

      if (this.briqueGridIsViewSelectedCount) this.briqueGridTag.querySelector('.brique-grid-selectedcount').innerText = '0'
    },

    /**
     * 그리드 초기화시
     */
    onGridReady (params) {
      this.gridApi = params.api
      this.gridColumnApi = params.columnApi
      let defColDef = this.$attrs['default-col-def']

      let columnDefs = this.briqueGridColumns

      // TODO: 이부분 어떻게???? (조건 좀 생각해 볼 것)
      function setFilter (col) {
        let type = col.type
        // if (!defColObjs['floatingFilter']) {
        if (_.isEmpty(type)) {
          col.filterFramework = 'stringFilter'
        } else if (type === 'numericColumn') {
          col.filterFramework = 'numberFilter'
        } else if (type === 'dateColumn' || type === 'dateTimeColumn') {
          col.filterFramework = 'dateFilter'
        } else {
          // numeric column이나 date column이 아닌 경우 string filter를 default로 사용
          col.filterFramework = 'stringFilter'
        }
      }
      function setEditor (col) {
        let type = col.type
        // if (!defColObjs['floatingFilter']) {
        if (_.isEmpty(type)) {
        } else if (type === 'numericColumn') {
          col.cellEditor = 'numericEditor'
        } else if (type === 'dateColumn') {
          col.cellEditor = 'datePickerEditor'
        } else if (type === 'dateTimeColumn') {
          col.cellEditor = 'dateTimePickerEditor'
        } else {
        }
      }
      columnDefs.forEach(col => {
        let required = col.required
        if (required === true) {
          col.headerClass = 'header-required'
        }
        // delete col.required

        let editable = col.modifiable
        if (editable === false) {
          col.editable = function (params) {
            return params.node.data.briqueGridCud === 'C'
          }
        }
        // delete col.modifiable

        setEditor(col)
        if (defColDef['filter'] || col['filter']) {
          setFilter(col)

          // Column header가 group으로 지정될 경우에는 children이 존재함
          let children = col.children
          if (!_.isEmpty(children)) {
            let newChildren = []
            for (let child of children) {
              setFilter(child)
              newChildren.push(child)
            }
            col.children = newChildren
          }
        }
      })

      columnDefs.push({
        field: 'briqueGridCud',
        hide: true
      })
      columnDefs.push({
        field: 'briqueGridSelected',
        hide: true
      })
      if (this.indexColumnVisible) {
        let self = this
        var serverSideValueGetter = function (params) {
          return Number(params.node.id) + 1 + Number(self.currentPage * self.pagingSize)
        }
        let indexColumn = {
          headerName: '',
          valueGetter: this.briqueGridPaginationType === 'client' ? 'Number(node.id) + 1' : serverSideValueGetter,
          width: 50,
          minWidth: 40,
          filter: '',
          flotingFilter: false,
          suppressMenu: true,
          pinned: 'left'
        }
        var index = _.findIndex(columnDefs, indexColumn)
        if (index < 0) {
          columnDefs.unshift({
            headerName: '',
            valueGetter: this.briqueGridPaginationType === 'client' ? 'Number(node.id) + 1' : serverSideValueGetter,
            width: 50,
            minWidth: 40,
            filter: '',
            flotingFilter: false,
            suppressMenu: true,
            pinned: 'left'
          })
        }
        // let indexColumn = { headerName: '', valueGetter: 'Number(node.id) + 1', width: 50, minWidth: 40, filter: '', flotingFilter: false, suppressMenu: true, pinned: 'left' }
        // var index = _.findIndex(columnDefs, indexColumn)
        // if (index < 0) {
        //   columnDefs.unshift({ headerName: '', valueGetter: 'Number(node.id) + 1', width: 50, minWidth: 40, filter: '', flotingFilter: false, suppressMenu: true, pinned: 'left' })
        // }
      }
      this.columns = columnDefs

      // let columnDefs = this.$attrs['column-defs']

      // // TODO: 이부분 어떻게???? (조건 좀 생각해 볼 것)
      // columnDefs.forEach(col => {
      //   console.log('column', col)
      //   let required = col.required
      //   let editable = col.modifiable
      //   if (required === true) {
      //     col.headerClass = 'header-required'
      //   }
      //   delete col.required
      //   if (editable === false) {
      //     col.editable = function (params) {
      //       return params.node.data.briqueGridCud === 'C'
      //     }
      //   }
      //   delete col.modifiable
      //   // if (col.required)
      // })

      // columnDefs.push({
      //   field: 'briqueGridCud',
      //   hide: true
      // })
      // columnDefs.push({
      //   field: 'briqueGridSelected',
      //   hide: true
      // })

      let agPagingPanel = this.briqueGridTag.querySelector('.ag-paging-panel')

      if (!this.briqueGridIsViewSummary) {
        this.briqueGridTag.querySelector('.ag-paging-row-summary-panel').classList.add('ag-hidden') // 내용은 보이지 않게
      }

      if (!this.briqueGridIsViewPagination) {
        this.briqueGridTag.querySelector('.ag-paging-page-summary-panel').classList.add('ag-hidden') // 내용은 보이지 않게
      } else if (this.briqueGridIsViewPagination && this.briqueGridPaginationType === 'server') {
        // Ag grid pagination panel hidden
        this.briqueGridTag.querySelector('.ag-paging-page-summary-panel').classList.add('ag-hidden') // 내용은 보이지 않게

        // pagination panel
        let pagePanel = document.createElement('span')
        pagePanel.classList.add('ag-paging-page-summary-panel')
        agPagingPanel.append(pagePanel)

        // btn first
        let btnFirst = document.createElement('div')
        btnFirst.id = this.briqueGridId + '-first-btn'
        btnFirst.classList.add('ag-paging-button')
        btnFirst.classList.add('ag-disabled')
        btnFirst.addEventListener('click', this.onFirstClick)
        pagePanel.append(btnFirst)

        let btnFirstSpan = document.createElement('span')
        btnFirstSpan.classList.add('ag-icon')
        btnFirstSpan.classList.add('ag-icon-first')
        btnFirst.append(btnFirstSpan)

        // btn previous
        let btnPrevious = document.createElement('div')
        btnPrevious.id = this.briqueGridId + '-previous-btn'
        btnPrevious.classList.add('ag-paging-button')
        btnPrevious.classList.add('ag-disabled')
        btnPrevious.addEventListener('click', this.onPreviousClick)
        pagePanel.append(btnPrevious)

        let btnPreviousSpan = document.createElement('span')
        btnPreviousSpan.classList.add('ag-icon')
        btnPreviousSpan.classList.add('ag-icon-previous')
        btnPrevious.append(btnPreviousSpan)

        let spanPagingDesc = document.createElement('span')
        spanPagingDesc.classList.add('ag-paging-description')
        pagePanel.append(spanPagingDesc)

        let spanStartPage = document.createElement('span')
        spanStartPage.id = this.briqueGridId + '-start-page'
        spanStartPage.innerText = 'Page'
        spanStartPage.classList.add('brique-grid-paging-desc')
        spanPagingDesc.append(spanStartPage)
        let spanStartPageNum = document.createElement('span')
        spanStartPageNum.id = this.briqueGridId + '-start-page-number'
        spanStartPageNum.classList.add('ag-paging-number')
        spanStartPageNum.classList.add('brique-grid-paging-desc')
        spanStartPageNum.innerText = '0'
        spanPagingDesc.append(spanStartPageNum)
        let spanOfPage = document.createElement('span')
        spanOfPage.id = this.briqueGridId + '-of-page'
        spanOfPage.innerText = 'Of'
        spanOfPage.classList.add('brique-grid-paging-desc')
        spanPagingDesc.append(spanOfPage)
        let spanOfPageNum = document.createElement('span')
        spanOfPageNum.id = this.briqueGridId + '-of-page-number'
        spanOfPageNum.classList.add('ag-paging-number')
        spanOfPageNum.classList.add('brique-grid-paging-desc')
        spanOfPageNum.innerText = '0'
        spanPagingDesc.append(spanOfPageNum)

        // btn next
        let btnNext = document.createElement('div')
        btnNext.id = this.briqueGridId + '-next-btn'
        btnNext.classList.add('ag-paging-button')
        btnNext.classList.add('ag-disabled')
        btnNext.addEventListener('click', this.onNextClick)
        pagePanel.append(btnNext)

        let btnNextSpan = document.createElement('span')
        btnNextSpan.classList.add('ag-icon')
        btnNextSpan.classList.add('ag-icon-next')
        btnNext.append(btnNextSpan)

        // btn last
        let btnLast = document.createElement('div')
        btnLast.id = this.briqueGridId + '-last-btn'
        btnLast.classList.add('ag-paging-button')
        btnLast.classList.add('ag-disabled')
        btnLast.addEventListener('click', this.onLastClick)
        pagePanel.append(btnLast)

        let btnLastSpan = document.createElement('span')
        btnLastSpan.classList.add('ag-icon')
        btnLastSpan.classList.add('ag-icon-last')
        btnLast.append(btnLastSpan)
      }

      let outerTag = document.createElement('div')
      outerTag.classList.add('brique-grid-cudcount')
      agPagingPanel.prepend(outerTag)

      if (this.briqueGridIsViewCudCount) {
        let cTag = document.createElement('span')
        cTag.classList.add('brique-grid-ccount')
        cTag.title = this.$t('fdc.GRID_COMPONENT.LABEL.INSERTED')
        cTag.innerText = '0'

        let uTag = document.createElement('span')
        uTag.classList.add('brique-grid-ucount')
        uTag.title = this.$t('fdc.GRID_COMPONENT.LABEL.MODIFIED')
        uTag.innerText = '0'

        let dTag = document.createElement('span')
        dTag.classList.add('brique-grid-dcount')
        dTag.title = this.$t('fdc.GRID_COMPONENT.LABEL.REMOVED')
        dTag.innerText = '0'

        outerTag.append(cTag)
        outerTag.append(' | ')
        outerTag.append(uTag)
        outerTag.append(' | ')
        outerTag.append(dTag)

        let btnSelectedView = document.createElement('button')
        btnSelectedView.classList.add('brique-grid-selected-rows-view-btn')
        btnSelectedView.title = this.$t('fdc.GRID_COMPONENT.BUTTON.TOGGLE_SELECTED_ROWS')
        btnSelectedView.addEventListener('click', (e) => {
          if (!e.target.classList.contains('active')) {
            this.briqueGridTag.querySelector('.brique-grid-updated-rows-view-btn').classList.remove('active')
            e.target.classList.add('active')
            this.gridApi.setFilterModel({ // 필터 설정
              briqueGridSelected: {
                filterType: 'text',
                type: 'equals',
                filter: 'true'
              }
            })
          } else {
            e.target.classList.remove('active')
            this.gridApi.setFilterModel(null) // 필터 해제
          }
        })

        let btnUpdatedView = document.createElement('button')
        btnUpdatedView.classList.add('brique-grid-updated-rows-view-btn')
        btnUpdatedView.title = this.$t('fdc.GRID_COMPONENT.BUTTON.TOGGLE_MODIFIED_ROWS')
        btnUpdatedView.addEventListener('click', (e) => {
          if (!e.target.classList.contains('active')) {
            this.briqueGridTag.querySelector('.brique-grid-selected-rows-view-btn').classList.remove('active')
            e.target.classList.add('active')
            this.gridApi.setFilterModel({ // 필터 설정
              briqueGridCud: {
                condition2: {
                  filterType: 'text',
                  type: 'equals',
                  filter: 'C' // 'U'
                },
                operator: 'OR',
                condition1: {
                  filterType: 'text',
                  type: 'equals',
                  filter: 'U' // 'U'
                }
              }
            })
          } else {
            e.target.classList.remove('active')
            this.gridApi.setFilterModel(null) // 필터 해제
          }
        })

        outerTag.append(btnSelectedView)
        outerTag.append(btnUpdatedView)
      }

      if (this.briqueGridIsViewSelectedCount || this.briqueGridIsViewTotalCount) {
        let outerTag = document.createElement('span')
        agPagingPanel.append(outerTag)

        if (this.briqueGridIsViewSelectedCount) {
          let selectedTag = document.createElement('span')
          selectedTag.classList.add('brique-grid-selectedcount')
          selectedTag.title = this.$t('fdc.GRID_COMPONENT.LABEL.SELECTED')
          selectedTag.innerText = '0'
          outerTag.append(selectedTag)
        }

        if (this.briqueGridIsViewSelectedCount && this.briqueGridIsViewTotalCount) {
          outerTag.append(' / ')
        }

        if (this.briqueGridIsViewTotalCount) {
          let totalTag = document.createElement('span')
          totalTag.innerText = '0'
          totalTag.classList.add('brique-grid-totalcount')
          totalTag.title = this.$t('fdc.GRID_COMPONENT.LABEL.TOTAL')
          outerTag.append(totalTag)
        }
      }
    },
    /**
     * 처음으로 button click event
     */
    onFirstClick () {
      this.$emit('changed-page', 0, this.pagingSize)
    },
    /**
     * 이전으로 button click event
     */
    onPreviousClick () {
      this.$emit('changed-page', this.currentPage - 1, this.pagingSize)
    },
    /**
     * 다음으로 button click event
     */
    onNextClick () {
      this.$emit('changed-page', this.currentPage + 1, this.pagingSize)
    },
    /**
     * 마지막으로 button click event
     */
    onLastClick () {
      this.$emit('changed-page', this.totalPage - 1, this.pagingSize)
    },
    /**
     * Pagination Description Setting & Pagination Button disabled Setting
     */
    setPagingDescBtn () {
      // 현재 page Of 전채 page
      document.getElementById(this.briqueGridId + '-of-page-number').innerText = this.totalPage
      document.getElementById(this.briqueGridId + '-start-page-number').innerText = this.currentPage + 1
      if (this.currentPage === 0) {
        // first, previous button disabled
        document.getElementById(this.briqueGridId + '-first-btn').classList.add('ag-disabled')
        document.getElementById(this.briqueGridId + '-previous-btn').classList.add('ag-disabled')
      } else {
        // disabled 해제
        document.getElementById(this.briqueGridId + '-first-btn').classList.remove('ag-disabled')
        document.getElementById(this.briqueGridId + '-previous-btn').classList.remove('ag-disabled')
      }

      if (this.currentPage === (this.totalPage - 1)) {
        // next, last button disabled
        document.getElementById(this.briqueGridId + '-next-btn').classList.add('ag-disabled')
        document.getElementById(this.briqueGridId + '-last-btn').classList.add('ag-disabled')
      } else {
        // disabled 해제
        document.getElementById(this.briqueGridId + '-next-btn').classList.remove('ag-disabled')
        document.getElementById(this.briqueGridId + '-last-btn').classList.remove('ag-disabled')
      }

      this.briqueGridTag.querySelector('.brique-grid-totalcount').innerText = this.totalElementCount
    },

    /**
     * 최초 Data Rendering시
     * @param {*} param
     */
    onFirstDataRendered ({ api, columnApi }) {
      this.oriData = _.cloneDeep(this.$attrs['row-data'])
      if (this.briqueGridIsViewTotalCount && !this.briqueGridIsViewPagination) {
        this.briqueGridTag.querySelector('.brique-grid-totalcount').innerText = api.getDisplayedRowCount()
      }

      this.removedData = []

      this.initBriquegridDisplayArea()

      if (!_.isEmpty(this.briqueGridHideToggleColumns)) { // 컬럼 보이기/숨기기 Toggle이 지정되어 있으면
        if (!this.briqueGridTag.querySelector('.brique-grid-togglecolumn-btn').classList.contains('active')) {
          this.briqueGridTag.querySelector('.brique-grid-togglecolumn-btn').click() // 곧바로 적용
        }
      }
    },

    /**
     * 데이터 수정시
     * @param {*} param
     */
    onRowDataUpdated ({ api, columnApi }) {
      if (this.briqueGridIsViewTotalCount) {
        this.briqueGridTag.querySelector('.brique-grid-totalcount').innerText = api.getDisplayedRowCount()
      }
    },

    /**
     * 행 선택 변경시
     * @param {*} param0
     */
    onSelectionChanged ({ api, columnApi }) {
      const selectedRows = api.getSelectedRows()

      // 부모 창으로 selected rows 전달
      this.$emit('brique-grid-selection-changed', selectedRows)

      if (this.briqueGridIsViewSelectedCount) this.briqueGridTag.querySelector('.brique-grid-selectedcount').innerText = selectedRows.length
      // selected row count data 설정
      this.checkedRowCount = selectedRows.length
    },

    /**
     * 행이 선택되거나 해제시
     */
    onRowSelected ({ node, data: rowData }) {
      rowData.briqueGridSelected = `${node.selected}`
    },

    /**
     * 셀의 값 변경시
     */
    async onCellValueChanged ({ oldValue, newValue, node, data: rowData, colDef }) {
      if (this.briqueGridCellValueChangedFunction) {
        await this.briqueGridCellValueChangedFunction(node, rowData, colDef)
      }
      if (rowData.briqueGridCud !== 'C' &&
       !_.isUndefined(oldValue) && !_.isNull(oldValue) &&
       !_.isUndefined(newValue) && !_.isNull(newValue)) {
        // FIXME: 실제 변화가 일어났는지 check후 변경
        rowData.briqueGridCud = 'U'
        node.setData(rowData)
        this.briqueGridTag.querySelector('.brique-grid-ucount').innerText = this.getUpdatedRowCount()
      }
    },

    /**
     * Import 버튼 클릭시
     */
    onClickImportCsv () {
      this.briqueGridTag.querySelector('[name=importCsvFile]').value = ''
      this.briqueGridTag.querySelector('[name=importCsvFile]').click()
    },

    /**
     * Import할 파일 선택창에서 파일 선택시
     * @param {*} e
     */
    async onChangeImportCsvFile (e) {
      const input = e.target.files[0]
      const reader = new FileReader()
      const self = this
      reader.onload = function (e) {
        const text = e.target.result
        const data = self.csvToArray(text)
        self.gridApi.setRowData(data)
        self.setAllRowsSelected()
        // document.write(JSON.stringify(data))
      }

      reader.readAsText(input)
    },
    /**
     * Csv data to array
     * @param {*} str csv string
     * @param {*} delimiter 구분자
     * @returns {Array} data array
     */
    csvToArray (str, delimiter = ',') {
      const headers = str.slice(0, str.indexOf('\n')).split(delimiter)

      const rows = str.slice(str.indexOf('\n') + 1).split('\n')

      const gridColumns = this.briqueGridColumns
      var arr = rows.map(function (row) {
        if (!_.isEmpty(row)) {
          const values = row.split(delimiter)
          const el = headers.reduce(function (object, header, index) {
            // const head = header.replaceAll('\"', '').replaceAll('\r', '')
            // let filterColumns = gridColumns.filter(c => c.headerName === head)
            // TODO: index로 field 찾는 방식 점검 필요
            let field = gridColumns[index]['field']
            if (!_.isEmpty(field) && field !== 'modifyDt' && field !== 'modifyUser') {
              object[field] = values[index].replaceAll('\"', '').replaceAll('\r', '')
            }
            return object
          }, {})
          return el
        }
      })
      // array 빈값 제거
      arr = arr.filter(data => !_.isEmpty(data))

      // return the array
      return arr
    },

    /**
     * Import 버튼 클릭시
     */
    onClickImportExcel () {
      this.briqueGridTag.querySelector('[name=importExcelFile]').value = ''
      this.briqueGridTag.querySelector('[name=importExcelFile]').click()
    },

    /**
     * Import할 파일 선택창에서 파일 선택시
     * @param {*} e
     */
    async onChangeImportExcelFile (e) {
      let blob = await e.target.files[0].arrayBuffer()
      let workbook = XLSX.read(blob, { type: 'binary' })

      let firstSheetName = workbook.SheetNames[0]
      let worksheet = workbook.Sheets[firstSheetName]

      const excelCols = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
      const defColObjs = this.$attrs['column-defs']

      // {
      //   'A': 'athlete',
      //   'B': 'age',
      //   'C': 'country',
      //   'D': 'year',
      //   'E': 'date',
      //   'F': 'sport',
      //   'G': 'gold',
      //   'H': 'silver',
      //   'I': 'bronze',
      //   'J': 'total'
      // }
      let columns = {}
      let index = 0
      for (const defColObj of defColObjs) {
        if (defColObj.hide) continue

        let excelCol = excelCols[index % 26]
        const index0 = Math.floor(index / 26)

        if (index0 > 0) {
          excelCol = excelCols[index0 - 1] + excelCol // AA, AB 등
        }

        columns[excelCol] = defColObj.field
        index++
      }

      let rowData = []

      // start at the 2nd row - the first row are the headers
      let rowIndex = 2

      // iterate over the worksheet pulling out the columns we're expecting
      while (worksheet['A' + rowIndex]) {
        let row = {}
        Object.keys(columns).forEach(function (column) {
          row[columns[column]] = worksheet[column + rowIndex].w
        })

        rowData.push(row)

        rowIndex++
      }

      // finally, set the imported rowData into the grid
      this.gridApi.setRowData(rowData)
    },

    /**
     * Export 버튼 클릭시
     */
    onClickExportCsv (fileName) {
      this.gridApi.exportDataAsCsv({
        fileName: fileName
      })

      // 아래는 enterprise 기능임..
      // this.gridApi.exportDataAsExcel({
      //   fileName: this.begridTitle || 'export',
      //   sheetName: this.begridTitle || 'sheet1'
      // })
    },

    /**
     * Refresh 버튼 클릭시
     */
    async onClickRefreshData () {
      await this.briqueGridRefreshFunction()
      this.initBriquegridDisplayArea()
    },

    /**
     * Restore 버튼 클릭시
     */
    onClickRestoreData () {
      this.gridApi.setRowData(_.cloneDeep(this.oriData))
      this.initBriquegridDisplayArea()
    },

    /**
     * Toggle Column 버튼 클릭시
     * @param {*} e
     */
    onClickToggleColumn (e) {
      // if (!e.target.classList.contains('active')) {
      if (!this.briqueGridTag.querySelector('.brique-grid-togglecolumn-btn').classList.contains('active')) {
        // e.target.classList.add('active')
        this.briqueGridTag.querySelector('.brique-grid-togglecolumn-btn').classList.add('active')

        this.gridColumnApi.setColumnsVisible(this.briqueGridHideToggleColumns, false)
      } else {
        // e.target.classList.remove('active')
        this.briqueGridTag.querySelector('.brique-grid-togglecolumn-btn').classList.remove('active')

        this.gridColumnApi.setColumnsVisible(this.briqueGridHideToggleColumns, true)
      }
    },

    /**
     * Add Row 버튼 클릭시
     */
    onClickAddRow () {
      let newRow = {}
      this.$emit('brique-grid-default-on-add', newRow)
      newRow.briqueGridCud = 'C'
      this.gridApi.applyTransaction({ add: [newRow], addIndex: 0 })

      this.briqueGridTag.querySelector('.brique-grid-ccount').innerText = this.getAddedRowCount()
    },

    /**
     * Add Row (one row) method
     * @param {*} newRow parent에서 정의한 row data object (default value setting을 위한 처리)
     */
    addRowOne (newRow, addIndex = 0) {
      newRow.briqueGridCud = 'C'
      this.gridApi.applyTransaction({ add: [newRow], addIndex: 0 })

      this.briqueGridTag.querySelector('.brique-grid-ccount').innerText = this.getAddedRowCount()
    },

    /**
     * Remove Row 버튼 클릭시
     */
    onClickRemoveRow () {
      let selectedRows = this.gridApi.getSelectedRows()
      this.gridApi.applyTransaction({ remove: selectedRows })
      this.$emit('row-data-changed')

      this.removedData = this.removedData.concat(selectedRows)

      selectedRows.forEach(function (selectedRow, index) {
        selectedRow.briqueGridCud = 'D'
      })

      this.briqueGridTag.querySelector('.brique-grid-ccount').innerText = this.getAddedRowCount()
      this.briqueGridTag.querySelector('.brique-grid-ucount').innerText = this.getUpdatedRowCount()
      this.briqueGridTag.querySelector('.brique-grid-dcount').innerText = this.getDeletedRowCount()
    },

    /**
     * Undo 버튼 클릭 시
     */
    onClickUndo () {
      // undoRedoCellEditingLimit는 default 10
      this.gridApi.undoCellEditing()
    },

    /**
     * Redo 버튼 클릭 시
     */
    onClickRedo () {
      // undoRedoCellEditingLimit는 default 10
      this.gridApi.redoCellEditing()
    },

    /**
     * 모든 행 불러오기
     */
    getAllRows () {
      let rowData = []
      this.gridApi.forEachNode(node => rowData.push(node.data))
      return rowData
    },

    /**
     * 추가된 행 가져오기
     */
    getAddedRows () {
      let { rowsToDisplay } = this.gridApi.getModel()
      let addedRows = []
      rowsToDisplay.forEach((row) => {
        if (row.data.briqueGridCud === 'C') {
          addedRows.push(row.data)
        }
      })
      return addedRows
    },

    /**
     * 추가된 행 수 구하기
     */
    getAddedRowCount () {
      let { rowsToDisplay } = this.gridApi.getModel()

      let count = rowsToDisplay.reduce((count, row) => {
        if (row.data.briqueGridCud === 'C') count++
        return count
      }, 0)

      return count
    },

    /**
     * 추가된 행 가져오기
     */
    getUpdatedRows () {
      let { rowsToDisplay } = this.gridApi.getModel()
      let updatedRows = []
      rowsToDisplay.forEach((row) => {
        if (row.data.briqueGridCud === 'U') {
          updatedRows.push(row.data)
        }
      })
      return updatedRows
    },

    /**
     * 수정된 행 수 구하기
     */
    getUpdatedRowCount () {
      let { rowsToDisplay } = this.gridApi.getModel()

      let count = rowsToDisplay.reduce((count, row) => {
        if (row.data.briqueGridCud === 'U') count++
        return count
      }, 0)

      return count
    },

    /**
     * 선택된 행 가져오기
     */
    getCheckedRows () {
      return this.gridApi.getSelectedRows()
    },

    /**
     * 삭제된 행수 구하기
     */
    getDeletedRowCount () {
      return this.removedData.length
    },

    /**
     * All Row Selection
     */
    setAllRowsSelected () {
      // let model = this.gridApi.getModel()
      this.gridApi.forEachNode(node => {
        node.setSelected(true)
      })
      // rowsToDisplay.forEach((row) => {
      //   row.setSelected(true)
      // })
    },

    /**
     * First Row Selection
     */
    setFirstRowSelected () {
      let select = false
      this.gridApi.forEachNode(node => {
        if (!select) {
          node.setSelected(true)
          select = true
        }
      })
    },

    /**
     * Grid Data 초기화
     */
    resetData () {
      this.checkedRowCount = 0
    },

    /**
     * Pagination change event
     * @param {*} pageNumber page number
     * FIXME: customizing 필요
     */
    changingPage (pageNumber) {
      let page = _.isUndefined(pageNumber) ? 0 : pageNumber
      let event = { cancel: false }
      this.$emit('changing-page', event)
      // pagination type 에서 page 변경을 할 수 없을 경우 page number 원복
      if (event.cancel) {
        let prevPageNum = this.paging.currentPageNumber
        this.$nextTick(function () {
          this.paging.currentPageNumber = prevPageNum
        })
      } else {
        this.changedPage(page - 1)
      }
    },
    changedPage (pageNumber) {
      this.$emit('changed-page', pageNumber, this.pagingSize)
    },

    /**
     * Filter open event
     */
    onFilterOpened (event) {
      console.log('filter open')
      // let { rowsToDisplay } = event.api.getModel()
      // console.log('filter open', rowsToDisplay)
    }
  }, // end - methods

  created () {
    this.briqueGridId = `BriqueGrid${this.$attrs.id}`
  }, // end - created

  mounted () {
    this.resetData()
    this.briqueGridTag = document.querySelector(`#${this.briqueGridId}`)
  }, // end - mounted

  data () {
    return {
      /** 그리드 ID */
      briqueGridId: null,

      /** 그리드 태그 */
      briqueGridTag: null,

      gridApi: null,
      gridColumnApi: null,

      /** 원본 데이터 */
      oriData: null,

      /** 삭제된 데이터 */
      removedData: [],

      /** Grid Columns */
      columns: [],

      /** Checked row count */
      checkedRowCount: 0,

      /** Column Type 정의 */
      columnTypes: {
        // nonEditableColumn: { editable: false },
        dateColumn: {
          // filter: 'agDateColumnFilter',
          valueFormatter: (params) => {
            let dateFormat = params.colDef.dateFormat
            if (!_.isEmpty(dateFormat)) {
              return _.isEmpty(params.value) ? params.value : (Dayjs(params.value).format(dateFormat))
            } else { // default format은 YYYY-MM-DD HH:mm:ss.SSS
              return _.isEmpty(params.value) ? params.value : (Dayjs(params.value).format('YYYY-MM-DD'))
            }
          }
        },
        dateTimeColumn: {
          // filter: 'agDateColumnFilter',
          valueFormatter: (params) => {
            let dateFormat = params.colDef.dateFormat
            if (!_.isEmpty(dateFormat)) {
              return _.isEmpty(params.value) ? params.value : (Dayjs(params.value).format(dateFormat))
            } else { // default format은 YYYY-MM-DD HH:mm:ss.SSS
              return _.isEmpty(params.value) ? params.value : (Dayjs(params.value).format('YYYY-MM-DD HH:mm:ss.SSS'))
            }
          }
        },
        checkboxColumn: {
          cellRenderer: function (params) {
            var input = document.createElement('input')
            input.type = 'checkbox'
            input.checked = params.value
            input.addEventListener('change', function (event) {
              params.value = !params.value
              // params.node.data[params.colDef.field] = params.value
              // params.node.data.briqueGridCud = 'U'
              let colId = params.column.colId
              params.node.setDataValue(colId, params.value)

              // mapping된 data가 있을 경우 설정된 true false에 맞게 data를 변경해 준다.
              let dataMappingObj = params.colDef.dataMapping
              if (!_.isEmpty(dataMappingObj)) {
                params.node.setDataValue(dataMappingObj.key, dataMappingObj[params.value])
              }
              // params.node.setDataValue('briqueGridCud', 'U')
              // this.briqueGridTag.querySelector('.brique-grid-ucount').innerText = this.getUpdatedRowCount()
            })
            return input
          }
          // params => {
          //   return `<input type='checkbox' ${params.value ? 'checked' : ''} />`
          // }
        },
        // TODO: demo성으로 구성된 column으로 추후 고도화 필요함
        radioButtonColumn: {
          cellRenderer: function (params) {
            var div = document.createElement('div')
            var input = document.createElement('input')
            input.type = 'radio'
            input.name = params.node.id + 'group'
            input.id = params.node.id + 'id'
            input.checked = params.value ? 'checked' : ''
            input.value = true
            input.style = 'margin-right: 10px'
            input.addEventListener('change', function (event) {
              params.value = !params.value
              // params.node.data[params.colDef.field] = params.value
              // params.node.data.briqueGridCud = 'U'
              let colId = params.column.colId
              params.node.setDataValue(colId, params.value)

              // mapping된 data가 있을 경우 설정된 true false에 맞게 data를 변경해 준다.
              let dataMappingObj = params.colDef.dataMapping
              if (!_.isEmpty(dataMappingObj)) {
                params.node.setDataValue(dataMappingObj.key, dataMappingObj[params.value])
              }
            })
            let dataMappingObj = params.colDef.dataMapping
            var label = document.createElement('label')
            label.for = params.node.id + 'id'
            label.style = 'margin-right: 10px'
            if (!_.isEmpty(dataMappingObj)) label.innerText = dataMappingObj['true']
            var input2 = document.createElement('input')
            input2.type = 'radio'
            input2.name = params.node.id + 'group'
            input2.checked = params.value ? '' : 'checked'
            input2.value = false
            input2.style = 'margin-right: 10px'
            input2.addEventListener('change', function (event) {
              params.value = !params.value
              let colId = params.column.colId
              params.node.setDataValue(colId, params.value)

              // mapping된 data가 있을 경우 설정된 true false에 맞게 data를 변경해 준다.
              let dataMappingObj = params.colDef.dataMapping
              if (!_.isEmpty(dataMappingObj)) {
                params.node.setDataValue(dataMappingObj.key, dataMappingObj[params.value])
              }
            })
            var label2 = document.createElement('label')
            label2.for = params.node.id + 'id'
            label2.style = 'margin-right: 10px'
            if (!_.isEmpty(dataMappingObj)) label2.innerText = dataMappingObj['false']
            div.appendChild(input)
            div.appendChild(label)
            div.appendChild(input2)
            div.appendChild(label2)
            return div
          }
        }
      },

      /** pagination 관련 data object */
      paging: {
        total: this.totalPage * this.pagingSize,
        totalPageNumber: this.totalPage,
        currentPageNumber: this.currentPage + 1, // currentPageNumber start from 1, currentPage start from 0
        perPage: this.pagingSize
      },

      /** Framwork Component */
      frameworkComponents: {},

      /** Row Class Rules */
      rowClassRules: {
        // add row class
        'brique-grid-added-row': function (params) { return params.data.briqueGridCud === 'C' },
        // modify row class
        'brique-grid-modify-row': function (params) { return params.data.briqueGridCud === 'U' }
      }
    }
  } // end - data
}
